/* eslint-disable @typescript-eslint/ban-ts-comment */
import Button from "../../Shared/Button"
import Input from "../../Shared/Input"
import TextArea from "../../Shared/TextArea"
import { useFormik } from "formik";
import { useMutation, useQuery } from "@tanstack/react-query";
import * as Yup from "yup";
import { AxiosError } from "axios";
import { ButtonTypes } from "../../../utils/consts";
import Spinner from "../../Shared/Spinner";
import { toast } from 'react-toastify';
import { queryClient } from "../../../main";
import Upload from "../../Shared/Upload";
import { GetAllInstructors } from "../../../api/Instructor/GetAllInstructors";
import Selectbox from "../../Shared/SelectBox";
import { useEffect, useState } from "react";
import { AddBookDTO, AddNewBook } from "../../../api/Books/AddBook";
import { GetBookBySlug } from "../../../api/Books/GetBookBySlug";
import { useParams, useNavigate } from "react-router-dom";
import { EditBook } from "../../../api/Books/EditBook";
import { Switch } from '@headlessui/react'
interface option {
  id?: string;
  name: string;
}

const validationSchema = Yup.object({
  name: Yup.string()
    .required("Book name is required"),
  description: Yup.string()
    .required("Book description is required"),
  img_url: Yup.string()
    .required("Display Image is required"),
  public_EGP_price: Yup.string()
    .required("Price is required"),
  public_USD_price: Yup.string()
    .required("Price is required"),
  authorId: Yup.string()
    .required("Author is required"),
});

interface ContentItem {
  id?: string;
  title: string;
  content: string;
}

const AddBook = ({ onSuccess }: { onSuccess?: () => void }) => {
  const [contents, setContents] = useState<ContentItem[]>([
    { title: '', content: '' }
  ]);
  const [enabled, setEnabled] = useState(false)
  const slug = useParams()
  const navigate = useNavigate()
  const bookSlug = slug.id || ''
  const [selected, setSelected] = useState<option | null>(null);
  const addContent = () => {
    setContents([...contents, { title: '', content: '' }]);
  };

  const removeContent = (index: number) => {
    const updatedContents = [...contents];
    updatedContents.splice(index, 1);
    setContents(updatedContents);
  };
  const { data: BookBySlugDTO, isLoading: isBookLoading } = useQuery({
    queryFn: () => GetBookBySlug(bookSlug),
    queryKey: ["books", bookSlug],
    staleTime: 0,
    refetchInterval: 0,
    enabled: !!bookSlug,
  });
  const book = BookBySlugDTO?.data;
  const bookId = book?.id || ''
  useEffect(() => {
    if (book) {
      setEnabled(book.isTrending || false);
    }
  }, [book]);

  useEffect(() => {
    if (book) {
      formik.setValues({
        name: book.name,
        description: book.description,
        img_url: book.img_url,
        public_EGP_price: String(book.public_EGP_price),
        public_USD_price: String(book.public_USD_price),
        authorId: book.authorId,
        isTrending: book.isTrending || enabled,
      });

      setContents(
        book.bookChapters.map((chapter) => ({
          id: chapter.id,
          title: chapter.title,
          content: chapter.content,
        }))
      );
  
      setSelected({
        id: book.author?.id,
        name: `${book.author?.firstName} ${book.author?.lastName}`,
      });
    }
  }, [book]);
  const { mutate, error, isPending } = useMutation({
    mutationFn: AddNewBook,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["books"] });
      toast.success("Book Added successfully");
      formik.resetForm();
      onSuccess && onSuccess();
    },
    onError: () => {
      formik.setSubmitting(false);
    },
  });
  const { mutate: editMutate, isPending: isEditPending } = useMutation({
    mutationFn: (data: AddBookDTO) => EditBook(data, bookId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["books"] });
      toast.success("Book Edited successfully");
      formik.setSubmitting(true);
      navigate('/dashboard/books')
    },
    onError: () => {
      formik.setSubmitting(false);
    },
  });

  const { data: InstructorDTO } = useQuery({
    queryFn: GetAllInstructors,
    queryKey: ["instructors"],
    staleTime: 0,
    refetchInterval: 0,
  });

  const Instructor = InstructorDTO?.data?.map(instructor => ({
    id: instructor.id,
    name: instructor.firstName + " " + instructor.lastName,
  })) || [];

  const formik = useFormik({
    initialValues: {
      name: "",
      description: "",
      img_url: "",
      public_EGP_price: "",
      public_USD_price: "",
      authorId: "",
      isTrending: book?.isTrending || enabled,
    },
    validationSchema,
    onSubmit: (values) => {
      const payload = {
        name: values.name,
        description: values.description,
        img_url: values.img_url,
        public_EGP_price: values.public_EGP_price,
        public_USD_price: values.public_USD_price,
        authorId: values.authorId,
        isTrending: enabled,
        bookChapters: contents.map(content => ({
          id: content.id,
          title: content.title,
          content: content.content,
        })),
      };
      if (bookSlug) {
        //@ts-expect-error
        editMutate(payload);
      } else {
        //@ts-expect-error
        mutate(payload);
      }
    },
  });
  const handleSelectInstructor = (option: option | null) => {
    setSelected(option);
    if (option) {
      formik.setFieldValue('authorId', option.id);
    } else {
      formik.setFieldValue('authorId', '');
    }
  };
  const handleTrendingToggle = (value: boolean) => {
    setEnabled(value);
    formik.setFieldValue("isTrending", value);
  };

  const handleimgUrlUpload = (url: string) => {
    formik.setFieldValue('img_url', url);
  };

  if (isBookLoading)
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner />
      </div>
    );

  return (
    <form onSubmit={formik.handleSubmit}
      className="pt-5">
      <div className="flex justify-between mb-6">
        <h1 className="text-burntBlack font-bold text-[32px] mt-5">
          {bookSlug ? 'Edit Book' : 'Upload New Book'}
        </h1>
      </div>
      <div className="p-8 rounded-3xl shadow bg-white border border-black/15 min-h-[70vh] flex gap-16">
        <div className="w-1/2">
          <h1 className="mb-2.5 font-bold text-2xl">Book info</h1>
          <p className="text-lightGray font-medium">Add Book details to help students discover your book and better
            understand what they will learn. what they will need and expect</p>
          <div className="space-y-7 pt-8">
            <Input
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.name && formik.errors.name
                  ? formik.errors.name
                  : ""
              }
              className="text-lg font-semibold"
              inputStyles="bg-[#F5F6F8] !h-12 border-black/15 !rounded"
              title="Book Title"
              placeholder=""
              name="name"
              type="text"
            />
            <TextArea
              value={formik.values.description}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.description && formik.errors.description
                  ? formik.errors.description
                  : ""
              }
              name="description" title="Book Description" placeholder="" inputStyles="!bg-[#F5F6F8] !h-[224px] border-black/15"
            />
            <div className="flex gap-5 pt-7">
              <Input
                value={formik.values.public_EGP_price}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.public_EGP_price && formik.errors.public_EGP_price
                    ? formik.errors.public_EGP_price
                    : ""
                }
                className="text-lg font-semibold"
                inputStyles="bg-[#F5F6F8] !h-12 border-black/15 !rounded"
                title="Price in EGP"
                placeholder=""
                name="public_EGP_price"
                type="text"
              />
              <Input
                value={formik.values.public_USD_price}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.public_USD_price && formik.errors.public_USD_price
                    ? formik.errors.public_USD_price
                    : ""
                }
                className="text-lg font-semibold"
                inputStyles="bg-[#F5F6F8] !h-12 border-black/15 !rounded"
                title="Price in USD"
                placeholder=""
                name="public_USD_price"
                type="text"
              />
            </div>
            <div className={`md:space-y-2 space-y-1.5 relative font-medium`}>
              <h1 className="text-burntBlack md:text-base text-sm">
                Is Trending
              </h1>
              <Switch
                checked={enabled}
                onChange={handleTrendingToggle}
                className={`${enabled ? 'bg-secondary' : 'bg-primary'}
          relative inline-flex h-[38px] w-[74px] shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2  focus-visible:ring-white/75`}
              >
                <span
                  aria-hidden="true"
                  className={`${enabled ? 'translate-x-9' : 'translate-x-0'}
            pointer-events-none inline-block h-[34px] w-[34px] transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out`}
                />
              </Switch>
            </div>

          </div>
        </div>
        <div className="w-1/2">
          <div className="space-y-7 pt-8">
            <Upload folder="books_images" onFileUploaded={handleimgUrlUpload}>
              <Input
                upload={true}
                className="text-lg font-semibold"
                inputStyles="bg-[#F5F6F8] !h-12 border-black/15 !rounded"
                placeholder=""
                title="Book Image"
                name="img_url"
                type="text"
                value={formik.values.img_url}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.img_url && formik.errors.img_url
                    ? formik.errors.img_url
                    : ""
                }
              />

            </Upload>
            <Selectbox title="Instructor" className="bg-[#F5F6F8] !h-12 border-black/15 !rounded" name="authorId" setSelected={handleSelectInstructor} selected={selected} selectOptions={Instructor} />
            {error instanceof AxiosError && (
              <span className="text-red-500 font-bold text-lg">
                {error?.response?.data?.message || "An error occurred"}
              </span>
            )}
            <div className="w-full">
              {contents.map((content, index) => (
                <div key={index} className="space-y-7 pt-8 blog-content">
                  <Input
                    value={content.title}
                    onChange={(e) => {
                      const updatedContents = [...contents];
                      updatedContents[index].title = e.target.value;
                      setContents(updatedContents);
                    }}
                    className="text-lg font-semibold"
                    inputStyles="bg-[#F5F6F8] !h-12 border-black/15 !rounded"
                    title={`Chapter Title ${index + 1}`}
                    placeholder=""
                    name={`contentTitle-${index}`}
                    type="text"
                  />
                  <TextArea
                    value={content.content}
                    onChange={(e) => {
                      const updatedContents = [...contents];
                      updatedContents[index].content = e.target.value;
                      setContents(updatedContents);
                    }}
                    name={`contentContent-${index}`}
                    title={`Chapter Description ${index + 1}`}
                    placeholder=""
                    inputStyles="!bg-[#F5F6F8] !h-[150px] border-black/15"
                  />
                  {index === 0 && ( // Ensure at least one content section exists
                    <div className="flex w-full justify-between mt-5">
                      <Button
                        type={ButtonTypes.BUTTON}
                        className="before:!bg-primary hover:text-white !text-base !font-bold !h-9 w-20 !border-primary !border-2 text-primary"
                        variant="transparent"
                        onClick={() => removeContent(index)}
                      >
                        Cancel
                      </Button>
                      <Button
                        type={ButtonTypes.BUTTON}
                        variant="primary"
                        className="!h-9 w-20 !text-base !font-bold"
                        onClick={addContent}
                      >
                        Add
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <Button className="!h-14 w-full !text-lg font-bold rounded-md !bg-[#4379EE] hover:!bg-primary" variant="primary" type={ButtonTypes.SUBMIT}
              disabled={formik.isSubmitting || isPending || isEditPending}
            >Done           {isPending ? (
              <Spinner />) : ('')}
            </Button>

          </div>

        </div>
      </div>
    </form>
  )
}

export default AddBook